/**
 * What a caught value really is, asked once instead of at every `catch`
 *
 * `useUnknownInCatchVariables` types a caught value as `unknown`, which is the truth: `throw` takes
 * anything, and an HTTP client can hand over an error, a string, or a rejected value of its own. Until
 * the switch was on it was `any`, so `e.response.data` read straight off it and nobody asked what `e`
 * was.
 *
 * These two functions are the narrowing, and they exist because the same two questions were written
 * out at **twenty-three** `catch` blocks across eight files: what body did the server send, and what
 * do we show when it sent none.
 *
 * WHY NOT `catch (e: any)`
 *
 * TypeScript admits it, and it would silence all twenty-three in one pass - which is exactly the
 * objection: it turns the switch off locally and leaves the reads unexamined. The same reasoning the
 * backend applied to `!` under `strictNullChecks`: a value that really can be anything is not made
 * safer by saying "trust me", only quieter.
 *
 * WHY IT SITS IN `frontend-shared/` AND NOT IN THE WEB
 *
 * It was written for the web, and then the counter and the self-service terminal went `strict` and
 * asked the same two questions at another thirteen `catch` blocks - `device/session.ts` among them,
 * which is shared code and must not reach into the web's `src/`. Two projects needing the same answer
 * is what this folder is for; a second copy is how the two answers start to differ.
 */

/**
 * The body of the answer, when the failure carried one.
 *
 * Axios puts the server's answer on `error.response.data`, and this code lives off that: the ticket
 * module answers `{ error, notice }` even when the status is not 200, and that body is the message the
 * player is shown. Anything else - a broken connection, a timeout - has no `response`, and then the
 * answer is `null` rather than an invented shape.
 */
export const bodyOf = (error: unknown): any =>
{
    const one = error as { response?: { data?: unknown } } | null | undefined;

    return (one != null && one.response != null && one.response.data != null) ? one.response.data : null;
};

/**
 * What to say when there is no body - **the same text as before**.
 *
 * Deliberately `String(error)`, which is what `e.toString()` produced at all six call sites: on an
 * `Error` both give `'Error: ...'`. The change is therefore type-only, and no sentence on any screen
 * moves. `String` and not the method, because a thrown `null` has no `toString` and a `catch` is the
 * one place where that is not hypothetical.
 *
 * `textOf` below is the other choice - the message **without** the `Error: ` prefix - and it is used
 * where the text goes to a person rather than into a `notice` field.
 */
export const messageOf = (error: unknown): string => String(error);

/**
 * The failure as a line of text.
 *
 * `message` where there is one, `String(error)` where there is not - because `String(new Error('x'))`
 * is `'Error: x'`, and a screen that says "Error: " twice over reads worse than the message alone.
 */
export const textOf = (error: unknown): string =>
{
    const one = error as { message?: unknown } | null | undefined;

    return (one != null && typeof one.message === 'string' && one.message !== '')
        ? one.message
        : String(error);
};

/**
 * The **reason**, as a key the screen can translate.
 *
 * The modules answer a refusal as `code` or `notice` - a translation key, not a sentence (see
 * `ErrorCodes`) - and only something that never reached a module has just a `message`. The order is
 * therefore the order of usefulness to whoever reads the screen.
 */
export const reasonOf = (error: unknown, fallback: string): string =>
{
    const one = error as { code?: unknown, notice?: unknown, message?: unknown } | null | undefined;

    if (one == null)
    {
        return fallback;
    }

    const found = one.code || one.notice || one.message;

    return (typeof found === 'string' && found !== '') ? found : fallback;
};
